import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Lazy-initialized admin client for API routes (service role key).
// Must NOT be created at module level — env vars aren't available at build time.
let _supabaseAdmin: SupabaseClient | null = null
export function getSupabaseAdmin(): SupabaseClient {
  if (!_supabaseAdmin) {
    _supabaseAdmin = createClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.SUPABASE_SERVICE_ROLE_KEY!,
      { auth: { autoRefreshToken: false, persistSession: false } },
    )
  }
  return _supabaseAdmin
}

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          email: string
          full_name: string | null
          avatar_url: string | null
          preferred_language: string | null
          referral_code: string
          referred_by: string | null
          is_pro: boolean | null
          is_admin: boolean | null
          pro_expires_at: string | null
          created_at: string | null
          updated_at: string | null
          subscription_plan: string | null
          subscription_status: string | null
          status: string | null
          voice_id: string | null
          voice_language: string | null
          voice_cloned_at: string | null
        }
        Insert: {
          id: string
          email: string
          full_name?: string | null
          avatar_url?: string | null
          preferred_language?: string | null
          referral_code: string
          referred_by?: string | null
          is_pro?: boolean | null
          is_admin?: boolean | null
          pro_expires_at?: string | null
          created_at?: string | null
          updated_at?: string | null
          subscription_plan?: string | null
          subscription_status?: string | null
          status?: string | null
          voice_id?: string | null
          voice_language?: string | null
          voice_cloned_at?: string | null
        }
        Update: {
          id?: string
          email?: string
          full_name?: string | null
          avatar_url?: string | null
          preferred_language?: string | null
          referral_code?: string
          referred_by?: string | null
          is_pro?: boolean | null
          is_admin?: boolean | null
          pro_expires_at?: string | null
          created_at?: string | null
          updated_at?: string | null
          subscription_plan?: string | null
          subscription_status?: string | null
          status?: string | null
          voice_id?: string | null
          voice_language?: string | null
          voice_cloned_at?: string | null
        }
      }
      rooms: {
        Row: {
          id: string
          name: string
          description: string | null
          created_by: string
          is_public: boolean
          max_participants: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          description?: string | null
          created_by: string
          is_public?: boolean
          max_participants?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          description?: string | null
          created_by?: string
          is_public?: boolean
          max_participants?: number
          created_at?: string
          updated_at?: string
        }
      }
      room_participants: {
        Row: {
          id: string
          room_id: string
          user_id: string
          joined_at: string
          left_at: string | null
          is_active: boolean
        }
        Insert: {
          id?: string
          room_id: string
          user_id: string
          joined_at?: string
          left_at?: string | null
          is_active?: boolean
        }
        Update: {
          id?: string
          room_id?: string
          user_id?: string
          joined_at?: string
          left_at?: string | null
          is_active?: boolean
        }
      }
      platform_metrics: {
        Row: {
          id: string
          metric_name: string
          metric_value: any
          recorded_at: string
          created_at: string
        }
        Insert: {
          id?: string
          metric_name: string
          metric_value: any
          recorded_at?: string
          created_at?: string
        }
        Update: {
          id?: string
          metric_name?: string
          metric_value?: any
          recorded_at?: string
          created_at?: string
        }
      }
      user_analytics: {
        Row: {
          id: string
          user_id: string
          event_type: string
          event_data: any
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          event_type: string
          event_data?: any
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          event_type?: string
          event_data?: any
          created_at?: string
        }
      }
      room_analytics: {
        Row: {
          id: string
          room_id: string
          participant_count: number
          duration_minutes: number
          created_at: string
        }
        Insert: {
          id?: string
          room_id: string
          participant_count: number
          duration_minutes: number
          created_at?: string
        }
        Update: {
          id?: string
          room_id?: string
          participant_count?: number
          duration_minutes?: number
          created_at?: string
        }
      }
    }
  }
}
